export type InputDevice = "keyboard" | "mouse" | "touch" | "gamepad";

export interface InputIntent {
    targetHeading: number | null;
    steer: number;
    pedal: boolean;
    brake: boolean;
    jump: boolean;
    boostToggle: boolean;
    spin: number;
    bell: boolean;
    pause: boolean;
    restart: boolean;
    device: InputDevice;
}

export interface KeyboardSample {
    left: boolean;
    right: boolean;
    pedal: boolean;
    brake: boolean;
    jump: boolean;
    boost: boolean;
    bell: boolean;
    pause: boolean;
    restart: boolean;
}

export interface PointerSample {
    dx: number;
    dy: number;
    clicked: boolean;
}

/** Joystick offset from its centre, each axis -1..1, y positive = down the screen. */
export interface StickSample {
    x: number;
    y: number;
}

export interface StickConfig {
    stickDeadZone: number;
    stickPedalY: number;
    stickBrakeY: number;
}

export interface PointerConfig {
    deadZone: number;
    pedalBelow: number;
    pedalWithPointer: boolean;
}

export const NO_KEYS: KeyboardSample = {
    left: false, right: false, pedal: false, brake: false, jump: false,
    boost: false, bell: false, pause: false, restart: false,
};

export const NO_POINTER: PointerSample = { dx: 0, dy: 0, clicked: false };

export const NO_STICK: StickSample = { x: 0, y: 0 };

export function mergeKeys(a: KeyboardSample, b: KeyboardSample): KeyboardSample {
    const merged = { ...a };
    for (const key of Object.keys(merged) as (keyof KeyboardSample)[]) merged[key] = a[key] || b[key];
    return merged;
}

export function pointerHeading(dx: number, dy: number, deadZone: number): number | null {
    if (Math.hypot(dx, dy) < deadZone) return null;
    const degrees = (Math.atan2(dx, dy) * 180) / Math.PI;
    if (Math.abs(degrees) <= 90) return degrees;
    return dx < 0 ? -90 : 90;
}

export function buildIntent(
    device: InputDevice,
    keys: KeyboardSample,
    pointer: PointerSample,
    cfg: PointerConfig,
): InputIntent {
    const usingPointer = device === "mouse";
    const pointerPedal = usingPointer && cfg.pedalWithPointer && pointer.dy > cfg.pedalBelow;

    return {
        targetHeading: usingPointer ? pointerHeading(pointer.dx, pointer.dy, cfg.deadZone) : null,
        steer: usingPointer ? 0 : Number(keys.right) - Number(keys.left),
        pedal: keys.pedal || pointerPedal,
        brake: keys.brake,
        jump: keys.jump || pointer.clicked,
        boostToggle: keys.boost,
        spin: 0,
        bell: keys.bell,
        pause: keys.pause,
        restart: keys.restart,
        device,
    };
}

export function stickAxis(value: number, deadZone: number): number {
    const size = Math.abs(value);
    if (size <= deadZone) return 0;
    return (Math.sign(value) * (size - deadZone)) / (1 - deadZone);
}

/**
 * The stick's sideways offset is the heading to aim for, so a centred stick eases the bike back
 * to straight downhill. Down pedals, up brakes. Keyboard steering still wins if held.
 */
export function applyStick(intent: InputIntent, stick: StickSample, cfg: StickConfig): InputIntent {
    const keyboardSteering = intent.steer !== 0;
    const heading = stickAxis(stick.x, cfg.stickDeadZone) * 90;
    return {
        ...intent,
        targetHeading: keyboardSteering ? intent.targetHeading : heading,
        pedal: intent.pedal || stick.y > cfg.stickPedalY,
        brake: intent.brake || stick.y < -cfg.stickBrakeY,
    };
}
